import { Add, Delete } from "@dreamhub/designsystem/icons";
import { INVALID_FIELD_BORDER_STYLE } from "@/components/controls/Form/Form";
import ConvertedCurrency from "@/pages/deals/components/commercials/ConvertedCurrency";
import { handleFieldError } from "./ContractTerms";
import { IconButton } from "@/components/ui/icon-button";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { Period as PeriodType } from "@/pages/deals/types";
import { cn } from "@/utils/utils";
import { has, isNumber } from "lodash";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  period: PeriodType;
  showTitle: boolean;
  tPrefix: string;
  showConversion: boolean;
  contractCurrencySymbol: string;
  onInputChange: (
    periodId: string,
    fieldName: keyof PeriodType,
    value: string,
    error: boolean,
    errorMessage?: string
  ) => void;
  onAddPeriod: (previousPeriod: PeriodType) => void;
  onDeletePeriod: (period: PeriodType) => void;
  contractDurationInMonths: number | null;
  rate: number | undefined;
  accountCurrencySymbol: string;
  totalAmount: number;
  allPeriodsAmounts: number;
  isOnlyPeriod?: boolean;
}

const Period: React.FC<Props> = ({
  period,
  showTitle,
  tPrefix,
  showConversion,
  contractCurrencySymbol,
  onInputChange,
  onAddPeriod,
  onDeletePeriod,
  contractDurationInMonths,
  rate,
  accountCurrencySymbol,
  totalAmount,
  allPeriodsAmounts,
  isOnlyPeriod = false
}) => {
  const { t } = useTranslation();

  const [fieldErrors, setFieldErrors] = useState<{
    [fieldName: string]: string;
  }>({});

  useEffect(() => {
    setFieldErrors({});
  }, []);

  const handleChangeInput = (fieldName: keyof PeriodType, value: string) => {
    let error = false;
    let errorMessage = "";
    if (fieldName === "endMonth") {
      const numericValue = parseInt(value, 10);
      if (period.startMonth && period.startMonth > numericValue) {
        error = true;
        errorMessage = t(`${tPrefix}.endMonthError`);
      }
      if (contractDurationInMonths) {
        if (
          isNaN(numericValue) ||
          numericValue < 1 ||
          numericValue > contractDurationInMonths
        ) {
          error = true;
          errorMessage = t(`${tPrefix}.timePeriodError`, {
            max: contractDurationInMonths
          });
        }
      }
    }
    if (fieldName === "amount") {
      const numericValue = Number(value);
      if (
        isNaN(numericValue) ||
        numericValue > totalAmount ||
        allPeriodsAmounts + numericValue > totalAmount
      ) {
        error = true;
        errorMessage = t(`${tPrefix}.totalAmountError`);
      }
    }
    handleFieldError(setFieldErrors, fieldName, error, errorMessage);
    return onInputChange(period.id, fieldName, value, error, errorMessage);
  };

  const handleAddPeriod = () => {
    return onAddPeriod(period);
  };

  const handleDeletePeriod = () => {
    return !isOnlyPeriod && onDeletePeriod(period);
  };

  const getInputErrorStyle = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      return cn(INVALID_FIELD_BORDER_STYLE, "text-error");
    }
    return "border-border-border2";
  };

  return (
    <div className="flex space-x-10">
      <div className="flex flex-col gap-4">
        {showTitle && (
          <Text variant="bodySecondary">{t(`${tPrefix}.months`)}</Text>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 w-[10rem] items-center">
            <Input
              value={period.startMonth || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeInput("startMonth", e.target.value)
              }
              className="border-border-border2 p-2 w-10 text-center"
              disabled
            />
            <Text variant="labelSecondary">-</Text>
            <Input
              value={period.endMonth || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeInput("endMonth", e.target.value)
              }
              className={cn(
                getInputErrorStyle("endMonth"),
                "p-2 w-10 text-center"
              )}
            />
          </div>
          {fieldErrors.endMonth && (
            <Text variant="smBodyError" className="mt-1">
              {fieldErrors.endMonth}
            </Text>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-4 w-full">
        {showTitle && (
          <Text variant="bodySecondary">{t(`${tPrefix}.amount`)}</Text>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between w-full">
            <div className="flex gap-2 items-center">
              <Input
                value={period.amount || ""}
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChangeInput("amount", e.target.value)
                }
                className="!border-border-border2 py-2 pr-2"
                leadingIcon={<>{contractCurrencySymbol}</>}
              />
              {showConversion && period.amount && isNumber(period.amount) ? (
                <ConvertedCurrency
                  amount={period.amount}
                  rate={rate}
                  accountCurrencySymbol={accountCurrencySymbol}
                />
              ) : null}
            </div>
            <div className="flex gap-1 items-center">
              <IconButton
                variant="iconNoBg"
                iconColor="secondary"
                size="md"
                onClick={handleDeletePeriod}
                disabled={isOnlyPeriod}
              >
                <Delete />
              </IconButton>
              <IconButton
                variant="iconNoBg"
                iconColor="secondary"
                size="md"
                onClick={handleAddPeriod}
                disabled={
                  !period.endMonth ||
                  has(fieldErrors, "endMonth") ||
                  period.endMonth === contractDurationInMonths
                }
              >
                <Add />
              </IconButton>
            </div>
          </div>
          {fieldErrors.amount && (
            <Text variant="smBodyError" className="mt-1">
              {fieldErrors.amount}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default Period;
